import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from '@mui/material';
import '../../App.css';
import SectionHeader from './SectionHeader';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const Sparkline = ({ type }) => {
  const path = type === 'up'
    ? "M 0 18 Q 15 5, 30 15 T 60 5 T 90 12"
    : "M 0 10 Q 15 25, 30 12 T 60 25 T 90 18";

  const color = type === 'up' ? '#4ADE80' : '#EF4444';
  const fillGradientId = type === 'up' ? 'sparkline-grad-up' : 'sparkline-grad-down';
  const closedPath = type === 'up'
    ? "M 0 18 Q 15 5, 30 15 T 60 5 T 90 12 L 90 30 L 0 30 Z"
    : "M 0 10 Q 15 25, 30 12 T 60 25 T 90 18 L 90 30 L 0 30 Z";

  return (
    <svg width="90" height="30" viewBox="0 0 90 30" style={{ overflow: 'visible', display: 'block' }}>
      <defs>
        <linearGradient id={fillGradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0.0" />
        </linearGradient>
      </defs>
      <path d={closedPath} fill={`url(#${fillGradientId})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const AssetTable = () => {
  const [rows, setRows] = useState([]);
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [tableRef, tableVisible] = useScrollAnimation(0.1);

  useEffect(() => {
    fetch('/assets.json')
      .then((res) => res.json())
      .then((data) => setRows(data))
      .catch((err) => console.error('Failed to load assets:', err));
  }, []);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 10, md: 10 },
        backgroundColor: '#0A080B',
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Box
          ref={headerRef}
          className={`anim-fade-up${headerVisible ? ' in-view' : ''}`}
        >
          <SectionHeader
            title={
              <>
                Supported Currencies &
                <br />
                Digital Assets
              </>
            }
            description="Payible supports leading cryptocurrencies and stablecoins, enabling fast and secure on/off ramps for individuals and companies."
            titleClassName="asset-title"
            descriptionClassName="asset-desc"
            titleSx={{
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "2.2rem",
              },
            }}
          />
        </Box>

        <TableContainer
          ref={tableRef}
          component={Paper}
          id="supported-currencies-table-container"
          sx={{
            backgroundColor: '#1C1C1F',
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: "var(--fs-card-background-shadow)",
          }}
        >
          <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <TableCell className="asset-table-header" sx={{ py: 2.5, pl: 3.5, borderBottom: 'none', textAlign: 'center', display: { xs: 'none', sm: 'table-cell' } }}>#</TableCell>
                <TableCell className="asset-table-header" sx={{ py: 2.5, borderBottom: 'none', textAlign: { xs: "center", md: "left" } }}>Coin Name</TableCell>
                <TableCell className="asset-table-header" sx={{ py: 2.5, borderBottom: 'none', textAlign: { xs: "center", md: "left" } }}>Symbol</TableCell>
                <TableCell className="asset-table-header" sx={{ py: 2.5, borderBottom: 'none', textAlign: { xs: "center", md: "left" } }}>Spot Price*</TableCell>
                <TableCell className="asset-table-header" sx={{ py: 2.5, pr: 3.5, borderBottom: 'none', textAlign: "center", display: { xs: 'none', sm: 'table-cell' } }}>24h Change</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  id={`table-row-coin-${row.symbol.toLowerCase()}`}
                  sx={{
                    opacity: tableVisible ? 1 : 0,
                    transform: tableVisible ? 'translateY(0)' : 'translateY(22px)',
                    transition: `opacity 1.05s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.09}s, transform 1.05s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.09}s, background-color 0.2s ease`,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    },
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  <TableCell className="asset-table-cell-id" sx={{ py: 2.5, pl: 3.5, borderBottom: 'none', textAlign: 'center', display: { xs: 'none', sm: 'table-cell' } }}>
                    {row.id}
                  </TableCell>
                  <TableCell className="asset-table-cell-name" sx={{ py: 2.5, pl: { xs: 1.5, sm: 3.5 }, pr: { xs: 0.5, sm: 1 }, borderBottom: 'none', textAlign: { xs: "center", md: "left" } }}>
                    {row.name}
                  </TableCell>
                  <TableCell className="asset-table-cell-symbol" sx={{ py: 2.5, px: { xs: 0.5, sm: 2 }, borderBottom: 'none', textAlign: { xs: "center", md: "left" } }}>
                    {row.symbol}
                  </TableCell>
                  <TableCell className="asset-table-cell-price" sx={{ py: 2.5, pr: { xs: 1.5, sm: 3.5 }, pl: { xs: 0.5, sm: 1 }, borderBottom: 'none', textAlign: { xs: "center", md: "left" } }}>
                    {row.price}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      pr: 3.5,
                      borderBottom: 'none',
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: 2, sm: 4 },
                        width: '100%',
                      }}
                    >
                      <Box sx={{ width: '90px' }}>
                        <Sparkline type={row.change} />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
};

export default AssetTable;
